import React, { useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'i18n'
import { SStyled, useSTheme } from 'styles/theme'
import SText from 'components/opinionless/SText'
import SBox from 'components/opinionless/SBox'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { ga4_useTitle } from 'utils/ga4'
import FImage from 'components/BaseComponents/FImage'
import style from './style.module.scss'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import moment from 'moment-timezone'
import { FormProvider, useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { ApplicationContext } from 'v2/context/application'
import ConditionalRender from '../../../../../utils/conditionalRender'
import FInputRadioGroup from 'components/BaseComponents/FInputRadioGroup'
import { useAddApplicationAnswersMutation } from 'types/graphql'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'

const LBTextHeader = SStyled(SText)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`

interface Props {
    setSelectedMember?: any
    setSelectedDocument: any
    fontFamily: any
    nextStep?: any
    onPrevious?: any
    updatePercentage: any
    disableBackButton?: boolean
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const DocumentsProofOfHouseholdIncomeStep1: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const form = useForm()
    const { t } = useTranslation(window.LANG_NS)
    const applicationContext = useContext(ApplicationContext)

    const [addApplicationAnswers] = useAddApplicationAnswersMutation()

    const { refetch: refetchApplicationAnswers } = useApplicationAnswers()

    useEffect(() => {
        const proofOfIncomeCategory = 'PROOF_OF_INCOME_CATEGORY'

        const foundAnswerForNoMember = applicationContext?.answers?.find(
            (itm) => itm.category === proofOfIncomeCategory && !itm.householdMember
        )

        if (applicationContext?.answers?.length && foundAnswerForNoMember) {
            const noMemberResponse = foundAnswerForNoMember.response
            form.setValue('document_category', noMemberResponse)
        }
    }, [applicationContext])

    const onSubmit = async (option, e) => {
        e.preventDefault()
        addApplicationAnswers({
            variables: {
                answers: [
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'PROOF_OF_INCOME_CATEGORY',
                        response: option.document_category,
                    },
                ],
            },
        }).then(() => {
            props.setSelectedDocument(`${option.document_category}_unstyled`)
            refetchApplicationAnswers()
            props.nextStep()
        })
    }

    const radioDataArray = useMemo(() => {
        return [
            {
                text: t('2022_income_tax_return'),
                id: '2022_income_tax_return',
            },
            {
                text: t('long_beach_pohi_one_paystub_width_3_months'),
                id: 'long_beach_pohi_one_paystub_width_3_months',
            },
            {
                text: t('long_beach_pohi_eligibility_proof_tanf_callworks_headstart'),
                id: 'long_beach_pohi_eligibility_proof_tanf_callworks_headstart',
            },
            {
                text: t('long_beach_pohi_letter_any_501(c)3_long_beach'),
                id: 'long_beach_pohi_letter_any_501(c)3_long_beach',
            },
            {
                text: t('long_beach_pohi_self_attestation_form'),
                id: 'long_beach_pohi_self_attestation_form',
            },
        ]
    }, [t])

    ga4_useTitle('Household income')

    const fontFamily = props.fontFamily

    const textStyles = {
        textAlignLast: 'left',
        whiteSpace: 'pre-line',
        fontFamily: fontFamily.body,
        lineHeight: '140%',
        color: theme.colors.gray400,
        fontSize: '14px',
        marginBottom: '5px',
        marginTop: '0px !important',
    }

    const applicantHasIncome = useMemo(() => {
        const earned = parseInt(applicationContext.profile.earnedIncome, 10) || 0
        const unearned = parseInt(applicationContext.profile.unearnedIncome, 10) || 0

        return earned || unearned
    }, [applicationContext.profile])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.documents,
        WIZARD_STEPS_MAPPING.documents.householdIncomeUploadOne,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    return (
        <FormProvider {...form}>
            <Container>
                <SBox as="form" onSubmit={form.handleSubmit(onSubmit)}>
                    <ConditionalRender if={!props.disableBackButton}>
                        <FButtonPrevious onClick={props.onPrevious} />
                    </ConditionalRender>{' '}
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.secondaryColor,
                            fontFamily: fontFamily.header,
                        }}
                    >
                        {t('long_beach_documents_pohi_header')}
                    </LBTextHeader>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '800',
                            lineHeight: '140%',
                            color: theme.colors.black,
                            fontSize: '16px',
                            marginBottom: '24px',
                        }}
                    >
                        {t('long_beach_documents_pohi_subheader')}
                    </LBText>
                    <ConditionalRender if={applicantHasIncome}>
                        <div style={{ display: 'flex' }}>
                            <FImage
                                imageUrl={'/lb_household_income_person.svg'}
                                alt="person"
                                width={'50px'}
                                height={'50px'}
                            />
                            <div style={{ marginLeft: '20px' }}>
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '700',
                                        lineHeight: '140%',
                                        color: theme.colors.lbBlue,
                                        fontSize: '14px',
                                        marginBottom: '5px',
                                        marginTop: '0 !important',
                                        textTransform: 'uppercase',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: t('long_beach_applicant_name_label_relationship', {
                                            name: `${applicationContext.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                            relationship: t('long_beach_household_applicant_label'),
                                        }),
                                    }}
                                />

                                <LBText sx={textStyles}>
                                    Date of Birth: {moment(applicationContext.profile.dob).format('MM/DD/YYYY')} (Age{' '}
                                    {moment().diff(applicationContext.profile.dob, 'years')})
                                </LBText>
                                <LBText sx={textStyles}>
                                    {!applicationContext.profile.blindDisabed
                                        ? t('ssi_benefits_wizard_blind_disabled_no')
                                        : t('ssi_benefits_wizard_blind_disabled_yes')}{' '}
                                </LBText>
                                <LBText sx={textStyles}>
                                    {applicationContext.profile.earnedIncome ? 'Income Earner' : 'Not Income Earner'}
                                </LBText>
                                <LBText sx={textStyles}>
                                    Earned Income: ${applicationContext.profile.earnedIncome || '0'}
                                </LBText>
                                <LBText sx={textStyles}>
                                    Unearned Income: ${applicationContext.profile.unearnedIncome || '0'}
                                </LBText>
                            </div>
                        </div>
                    </ConditionalRender>
                    {applicationContext.householdMembers.map((itm, key) => {
                        if (!parseInt(itm.earnedIncome, 10) && !parseInt(itm.unearnedIncome, 10)) {
                            return null
                        }

                        return (
                            <div key={key}>
                                <div style={{ display: 'flex' }}>
                                    <FImage
                                        imageUrl={'/lb_household_income_person_bw.svg'}
                                        alt="person_bw"
                                        width={'50px'}
                                        height={'50px'}
                                    />
                                    <div style={{ marginLeft: '20px' }}>
                                        <LBText
                                            sx={{
                                                textAlignLast: 'left',
                                                whiteSpace: 'pre-line',
                                                fontFamily: fontFamily.body,
                                                fontWeight: '700',
                                                lineHeight: '140%',
                                                color: theme.colors.gray400,
                                                fontSize: '14px',
                                                marginBottom: '5px',
                                                marginTop: '0 !important',
                                                textTransform: 'uppercase',
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: t('long_beach_applicant_name_label_relationship', {
                                                    name: `${itm?.name}`.toUpperCase(),
                                                    relationship: t(`${itm?.relation}`),
                                                }),
                                            }}
                                        />

                                        <LBText sx={textStyles}>
                                            Date of Birth: {moment(itm.dob, 'YYYY-MM-DD').format('MM/DD/YYYY')} (Age:{' '}
                                            {moment().diff(moment(itm.dob, 'YYYY-MM-DD').format('MM/DD/YYYY'), 'years')}
                                            )
                                        </LBText>
                                        <LBText sx={textStyles}>
                                            {!itm.blindDisabed
                                                ? t('ssi_benefits_wizard_blind_disabled_no')
                                                : t('ssi_benefits_wizard_blind_disabled_yes')}
                                        </LBText>
                                        <LBText sx={textStyles}>
                                            {itm.earnedIncome ? 'Income Earner' : 'Not Income Earner'}
                                        </LBText>

                                        <LBText sx={textStyles}>Earned Income: ${itm.earnedIncome || '0'}</LBText>
                                        <LBText sx={textStyles}>Unearned Income: ${itm.unearnedIncome || '0'}</LBText>
                                    </div>
                                    <div
                                        className={style.household_applicant_blind_disabled_container__applicant_group}
                                    ></div>
                                </div>
                            </div>
                        )
                    })}
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                            marginBottom: '24px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_documents_pohi_description'),
                        }}
                    />
                    <FInputRadioGroup
                        radioDataArray={radioDataArray}
                        name={'document_category'}
                        id={'document_category'}
                        minimumSelection={1}
                    />
                    <LBButton
                        type={'submit'}
                        id={'continueButton'}
                        title={t('save_and_continue')}
                        width={'100%'}
                        mt={7}
                        mb={5}
                        textStyle={{
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '140%',
                            fontFamily: fontFamily.body,
                            color: theme.colors.white,
                        }}
                    >
                        {t('save_and_continue')}
                    </LBButton>
                </SBox>
            </Container>
        </FormProvider>
    )
}

export default DocumentsProofOfHouseholdIncomeStep1
